import { Icon } from "../Icon"
import classNames from "classnames"
import { ElementType, useMemo } from "react"
import { Tooltip } from "../Tooltip"
import type { FormLabelProps } from "./types"
import { noop } from "lodash-es"
import { OysterIcon } from "@oysterhr/icons"

interface ClickableTooltipProps {
  onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  iconClassnames?: string
  icon?: OysterIcon
  children?: React.ReactNode
}

// TODO: Remove this in favor of endAdornment
const ClickableTooltip = ({ onClick, children, icon, iconClassnames }: ClickableTooltipProps) => {
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="clickable-tooltip"
      className="cursor-pointer"
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
        onClick(e)
      }}
      onKeyDown={noop}
    >
      {!children && (
        <Icon
          name={icon || "InfoCircle"}
          className={classNames(
            iconClassnames || "text-primary-base hover:text-primary-darken-100",
          )}
        />
      )}
      {children}
    </div>
  )
}

export const FormLabel = <E extends ElementType = "label">({
  as,
  assistiveText,
  className,
  endAdornment = null,
  isOptional = false,
  label,
  optionalText = "(optional)",
  tooltip,
  htmlFor,
  ...rest
}: FormLabelProps<E>) => {
  const renderedTooltip = useMemo(() => {
    if (!tooltip) {
      return null
    }
    if (typeof tooltip === "function") {
      return (
        <span className="ml-2">
          <ClickableTooltip onClick={tooltip} />
        </span>
      )
    }
    return (
      <Tooltip value={tooltip}>
        <span data-testid={`tooltip-icon-${htmlFor}`}>
          <Icon className="ml-2" name="WarningCircle" title="Info tooltip" />
        </span>
      </Tooltip>
    )
  }, [tooltip, htmlFor])

  if (!label) {
    return null
  }

  const Component = as || "label"

  /**
   * The optional element that is rendered if the field is optional
   */
  const optionalElement = isOptional ? (
    <>
      {" "}
      <span className={classNames("text-gray-300")}>{optionalText}</span>
    </>
  ) : null

  return (
    <Component
      className={classNames("flex flex-col text-base font-medium leading-6 text-black", className)}
      htmlFor={htmlFor}
      {...rest}
    >
      <div className="flex flex-row items-center">
        <span>
          {label}
          {optionalElement}
        </span>
        {renderedTooltip}
        {endAdornment ? <div className="ml-2">{endAdornment}</div> : null}
      </div>
      {!!assistiveText && typeof assistiveText === "string" ? (
        <span className="text-sm font-normal leading-4 text-gray-400">{assistiveText}</span>
      ) : (
        assistiveText
      )}
    </Component>
  )
}

export const FORM_LABEL_DISPLAY_NAME = "FormLabel"

FormLabel.displayName = FORM_LABEL_DISPLAY_NAME
